import React, { useEffect, useState, useRef } from "react";
import {
  Briefcase,
  X as XIcon,
  Send,
  Eye,
  MessageCircleMore,
  UsersRound,
  FolderOpen,
  FolderPlus,
} from "lucide-react";

const HEALTH_BADGE = {
  NENHUMA: {
    label: "Estável",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  DOENCA_GRAVE: {
    label: "Doença Grave",
    cls: "bg-red-50 text-red-700 border-red-200",
  },
  DEFICIENCIA_FISICA: {
    label: "PcD (Física)",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
  },
  DEFICIENCIA_MENTAL: {
    label: "PcD (Mental/Intelectual)",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
  },
  AUTISMO: {
    label: "PcD (Espectro Autista - TEA)",
    cls: "bg-purple-50 text-purple-700 border-purple-200",
  },
  IDOSO: {
    label: "Idoso (60+)",
    cls: "bg-blue-50 text-blue-700 border-blue-200",
  },
  SUPER_IDOSO: {
    label: "Super Idoso (80+)",
    cls: "bg-blue-50 text-blue-700 border-blue-200",
  },
};

const calcAge = (birthDate) => {
  if (!birthDate) return null;
  const [y, m, d] = String(birthDate).split("-").map(Number);
  if (!y) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  if (
    today.getMonth() + 1 < m ||
    (today.getMonth() + 1 === m && today.getDate() < d)
  )
    age--;
  return age;
};

const TABS = [
  { id: "CADASTRO", label: "Cadastro" },
  { id: "PRONTUARIO", label: "Prontuário" },
  { id: "AUTOS", label: "Autos & Processos" },
  { id: "ACERVO", label: "Publicações" },
  { id: "FINANCEIRO", label: "Financeiro" },
];

const ClientSidePeek = ({
  isOpen,
  client,
  activeTab,
  setActiveTab,
  onCloseRequest,
  children,
  mattersCount = 0,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRequest?.();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [isOpen, onCloseRequest]);

  // --- Dados do nome ---
  const nameData = (() => {
    if (!client) return { socialName: "", name: "" };
    const socialName =
      client?.qualification?.physical?.socialName || client?.socialName || "";
    const name =
      client?.qualification?.physical?.name ||
      client?.name ||
      client?.qualification?.legal?.companyName ||
      client?.qualification?.minor?.name ||
      client?.qualification?.animal?.name ||
      "";
    return {
      socialName: String(socialName || "").trim(),
      name: String(name || "").trim(),
    };
  })();
  const nomeDisplay = nameData?.socialName || nameData?.name || "Novo Cliente";
  const showCivil = Boolean(
    nameData?.socialName &&
    nameData?.name &&
    nameData.socialName !== nameData.name,
  );

  // --- Badges de Saúde ---
  const rawHealth = client?.healthCondition;
  const healthKeys = Array.isArray(rawHealth)
    ? rawHealth.filter(
        (c) => c && c !== "NENHUMA" && c !== "IDOSO" && c !== "SUPER_IDOSO",
      )
    : rawHealth &&
        rawHealth !== "NENHUMA" &&
        rawHealth !== "IDOSO" &&
        rawHealth !== "SUPER_IDOSO"
      ? [String(rawHealth)]
      : [];

  const clientAge = calcAge(client?.qualification?.physical?.birthDate);
  const isSuperIdoso = typeof clientAge === "number" && clientAge >= 80;
  const isIdoso =
    !isSuperIdoso && typeof clientAge === "number" && clientAge >= 60;

  const ageBadges = isSuperIdoso
    ? [HEALTH_BADGE.SUPER_IDOSO]
    : isIdoso
      ? [HEALTH_BADGE.IDOSO]
      : [];
  const allBadges = [
    ...healthKeys.map(
      (k) =>
        HEALTH_BADGE[k] || {
          label: k,
          cls: "bg-slate-50 text-slate-700 border-slate-200",
        },
    ),
    ...ageBadges,
  ];
  const healthBadges = allBadges.length > 0 ? allBadges : null;
  const hasPriority = healthKeys.length > 0 || isIdoso || isSuperIdoso;

  // --- Ações ---
  const readText = (value) => String(value || "").trim();
  const resolvePhone = (c) =>
    readText(
      c?.contact?.phonePrimary ||
        c?.contact?.phone ||
        c?.phone ||
        c?.whatsapp ||
        "",
    );
  const normalizeWhatsappNumber = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.startsWith("55") ? digits : `55${digits}`;
  };
  const whatsappNumber = normalizeWhatsappNumber(resolvePhone(client));
  const clientEmail = readText(client?.contact?.email || "");
  const clientId = readText(client?.id);
  const resolveDriveFolderId = (c) =>
    readText(
      c?.driveFolderId ||
        c?.drive?.folderId ||
        c?.integrations?.googleDrive?.folderId,
    );
  const driveFolderId = resolveDriveFolderId(client);
  const driveFolderUrl = driveFolderId
    ? `https://drive.google.com/drive/folders/${driveFolderId}`
    : "";

  // Tooltip wrapper (inline para evitar dependência externa).
  // align="right": ancora a ponta direita do texto na ponta direita do
  // gatilho, crescendo pra esquerda — usar em qualquer botão perto da borda
  // direita do painel, pra nunca vazar pra fora da tela (achado 14/09/2026).
  const Tooltip = ({ text, children, align = "center" }) => (
    <span className="relative group">
      {children}
      <span
        className={
          "absolute -top-8 whitespace-nowrap rounded bg-slate-800 text-white text-[9px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 " +
          (align === "right" ? "right-0" : "left-1/2 -translate-x-1/2")
        }
      >
        {text}
      </span>
    </span>
  );

  return (
    <div className="fixed inset-0 z-[300]">
      {/* Overlay */}
      <div
        className={
          "fixed inset-0 bg-black/30 z-[300] " +
          (isOpen ? "opacity-100" : "opacity-0 pointer-events-none")
        }
        onClick={onCloseRequest}
      />

      {/* Painel */}
      <div
        className={
          "fixed top-0 right-0 w-full max-w-[95vw] md:w-[720px] lg:w-[900px] xl:w-[1040px] h-[100dvh] bg-white z-[310] " +
          (isOpen ? "translate-x-0" : "translate-x-full")
        }
        role="dialog"
        aria-modal="true"
      >
        <div className="h-full flex flex-col">
          {/* === LINHA 1: Badge + Nome + Badges Saúde + Fechar === */}
          <div className="h-12 min-h-[48px] flex items-center gap-2 px-4 border-b border-slate-200 bg-white">
            {/* Badge Prioritário */}
            {hasPriority && (
              <span className="shrink-0 inline-flex items-center gap-1 bg-red-600 text-white px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest">
                🚨 Prioritário
              </span>
            )}

            {/* Nome do cliente */}
            <div className="min-w-0 flex-1 flex items-center gap-2">
              <span
                className="text-sm font-extrabold text-[#20243B] truncate"
                title={nomeDisplay}
              >
                {nomeDisplay}
              </span>
              {showCivil && (
                <span
                  className="text-[10px] text-slate-400 truncate hidden sm:inline"
                  title={nameData?.name}
                >
                  ({nameData?.name})
                </span>
              )}
            </div>

            {/* Badges de saúde com scroll */}
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar max-w-[200px] shrink-0">
              {healthBadges ? (
                healthBadges.map((badge, i) => (
                  <span
                    key={i}
                    className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${badge.cls}`}
                  >
                    {badge.label}
                  </span>
                ))
              ) : (
                <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] text-slate-400 bg-slate-50 border border-slate-100">
                  Sem condições especiais
                </span>
              )}
            </div>

            {/* driveTreeHealthy widget */}
            <div className="shrink-0">
              {client?.driveTreeHealthy === true ? (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border bg-emerald-50 text-emerald-700 border-emerald-200"
                  title="Estrutura de pastas do Drive íntegra"
                >
                  <i className="fas fa-folder-tree text-[8px]" />
                  Drive OK
                </span>
              ) : client?.driveTreeHealthy === false ? (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border bg-red-50 text-red-700 border-red-200"
                  title="Problemas detectados na estrutura de pastas do Drive"
                >
                  <i className="fas fa-exclamation-triangle text-[8px]" />
                  Drive c/ Problemas
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border bg-slate-50 text-slate-400 border-slate-200"
                  title="Integridade do Drive não verificada"
                >
                  <i className="fas fa-folder-open text-[8px]" />
                  Drive não verificado
                </span>
              )}
            </div>

            {/* Métricas compactas */}
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-slate-400 shrink-0">
              <span className="w-px h-3 bg-slate-200" />
              <Briefcase className="size-2.5" />
              {mattersCount}
            </span>

            {/* Botão Fechar (X) */}
            <Tooltip text="Fechar (Esc)" align="right">
              <button
                type="button"
                onClick={onCloseRequest}
                className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <XIcon className="size-4" />
              </button>
            </Tooltip>
          </div>

          {/* === LINHA 2: Abas + Button Group Ações === */}
          <div className="h-10 min-h-[40px] flex items-center justify-between gap-2 px-4 border-b border-slate-200 bg-slate-50/80">
            {/* Abas de navegação */}
            <div className="flex items-center gap-0.5 overflow-x-auto no-scrollbar">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={
                    "px-2.5 py-1.5 rounded-md text-[10px] font-extrabold uppercase tracking-widest whitespace-nowrap transition-colors " +
                    (activeTab === t.id
                      ? "bg-white text-[#20243B] border border-slate-300 shadow-sm"
                      : "text-slate-500 hover:text-slate-800 hover:bg-white/60 border border-transparent")
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Botões de ação — todos visíveis lado a lado (sem menu escondido),
                agrupados por linha vertical entre grupos diferentes: Drive |
                Comunicação | Preview/Acesso. Painel tem espaço de sobra pra
                mostrar tudo direto, sem precisar de dropdown. */}
            <div className="flex items-center gap-1 shrink-0 rounded-md border border-slate-200 bg-slate-100 px-1 py-1">
              {/* Drive */}
              <Tooltip
                align="right"
                text={
                  driveFolderUrl
                    ? "Abrir Google Drive"
                    : "Orquestrar Google Drive"
                }
              >
                <button
                  type="button"
                  onClick={() => {
                    if (driveFolderUrl)
                      window.open(
                        driveFolderUrl,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    else
                      window.dispatchEvent(
                        new CustomEvent("rda:orquestrar-drive", {
                          detail: { clientId, clientName: nomeDisplay },
                        }),
                      );
                  }}
                  className="p-1.5 rounded text-slate-500 hover:text-slate-800 hover:bg-white transition-colors"
                >
                  {driveFolderUrl ? (
                    <FolderOpen className="size-3.5" />
                  ) : (
                    <FolderPlus className="size-3.5" />
                  )}
                </button>
              </Tooltip>

              <span className="w-px h-4 bg-slate-300 mx-0.5" />

              {/* Grupo Comunicação */}
              <Tooltip text="WhatsApp Individual" align="right">
                <button
                  type="button"
                  onClick={() => {
                    if (!whatsappNumber) return;
                    window.open(
                      `https://wa.me/${whatsappNumber}`,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                  disabled={!whatsappNumber}
                  className="p-1.5 rounded text-slate-500 hover:text-slate-800 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <MessageCircleMore className="size-3.5 text-emerald-600" />
                </button>
              </Tooltip>
              <Tooltip text="Copiar convite de Grupo WhatsApp" align="right">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `Olá! Vamos iniciar o atendimento de ${nomeDisplay}. Crie um grupo no WhatsApp com o cliente e equipe.`,
                    );
                  }}
                  className="p-1.5 rounded text-slate-500 hover:text-slate-800 hover:bg-white transition-colors"
                >
                  <UsersRound className="size-3.5 text-blue-600" />
                </button>
              </Tooltip>

              <span className="w-px h-4 bg-slate-300 mx-0.5" />

              {/* Grupo Preview/Acesso */}
              <Tooltip text="Ver Portal (modo preview)" align="right">
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent("rda:preview-portal", {
                        detail: { clientId, clientName: nomeDisplay },
                      }),
                    );
                  }}
                  disabled={!clientId}
                  className="p-1.5 rounded text-slate-500 hover:text-slate-800 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Eye className="size-3.5 text-violet-600" />
                </button>
              </Tooltip>
              <Tooltip text="Enviar Acesso Mágico (magic link)" align="right">
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(
                      new CustomEvent("rda:enviar-magic-link", {
                        detail: {
                          clientId,
                          email: clientEmail,
                          clientName: nomeDisplay,
                        },
                      }),
                    );
                  }}
                  disabled={!clientEmail}
                  className="p-1.5 rounded text-slate-500 hover:text-slate-800 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send className="size-3.5 text-[#C5A059]" />
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Conteúdo principal (children) */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-slate-50">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientSidePeek;
